import type {FilterTile} from '@/modules/common/components/Filter/FilterTile';
import type {OptionFilterItem} from '@/modules/common/models/filters';
import type {ExtractPropsWithoutRef} from '@/types';

import {Text, useBreakpointDown} from '@travel-frontend/core';
import {Flex, Radio} from '@travel-frontend/ui-components';
import {useTranslation} from 'react-i18next';

import {Collapsible} from '@/components/Collapsible';
import {HelpTooltip} from '@/components/HelpTooltip';
import {NewFeatureBadge} from '@/components/NewFeatureBadge';
import {config} from '@/config';
import {Headline} from '@/modules/common/components/Filter/DistanceFilter/components/Headline';
import {Label} from '@/modules/common/components/Filter/DistanceFilter/components/Label';

type FilterTileProps = Pick<
  ExtractPropsWithoutRef<typeof FilterTile>,
  'helpArticle' | 'selectedFiltersCount' | 'caption'
>;

type Props = ExtractPropsWithoutRef<'div'> &
  FilterTileProps & {
    options: Array<OptionFilterItem>;
    canShowOptionBestPrice?: boolean;
    onChange: (param: {type: 'clear' | 'set'; value: string | null}) => void;
    tooltip?: string;
    isDefaultExpanded?: boolean;
  };

export const DistanceFilter = ({
  options,
  canShowOptionBestPrice,
  onChange,
  caption,
  tooltip,
  isDefaultExpanded,
  ...restProps
}: Props) => {
  const {t} = useTranslation(['general', 'filters']);
  const mdDown = useBreakpointDown('md');

  const handleChange = (value: string) => () => {
    if (value) {
      onChange({type: 'set', value});
    } else {
      onChange({type: 'clear', value: null});
    }
  };

  const items = options.map((item) => (
    <Radio
      size={mdDown ? 'medium' : 'small'}
      label={
        <Flex
          flex={1}
          justifyContent="space-between"
          alignItems="baseline"
          direction="row"
        >
          <Flex spacing={0}>
            <Label checked={item.active}>
              {item.value
                ? t('filters:distance.explanationLabel.normal', {
                    distance: item.value,
                  })
                : t('filters:distance.explanationLabel.default')}
            </Label>
          </Flex>
          {canShowOptionBestPrice && (
            <Text
              preset={mdDown ? 'labelSmall' : 'labelXSmall'}
              color="contentSecondary"
            >
              {t('general:bestPrice', {
                price: item.priceMin?.amount,
                currency: item.priceMin?.currency,
              })}
            </Text>
          )}
        </Flex>
      }
      key={item.value}
      checked={item.active}
      disabled={!item.available}
      data-testid={item.active ? 'item-selected' : 'item'}
      onChange={item.available ? handleChange(item.value) : undefined}
    />
  ));

  return (
    <Collapsible
      {...restProps}
      isCollapsible={true}
      isCollapsed={!isDefaultExpanded}
      maxVisibleItems={items.length}
      headline={
        <Headline>
          {config.filters.distances.showNewFeatureBadge && <NewFeatureBadge />}
          {caption}
          {tooltip && <HelpTooltip placement="bottom" content={tooltip} />}
        </Headline>
      }
    >
      {items}
    </Collapsible>
  );
};
